import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Box,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Sort as SortIcon
} from '@mui/icons-material';

export interface ColumnDefinition<T = any> {
  key: string;
  label: string;
  sortKey?: string;
  sortable?: boolean;
  align?: 'left' | 'center' | 'right';
  render?: (item: T) => React.ReactNode;
  width?: string | number;
}

export interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

export interface DataTableProps<T = any> {
  data: T[];
  columns: ColumnDefinition<T>[];
  sortConfig: SortConfig;
  onSort: (key: string) => void;
  onRowClick?: (item: T) => void;
  
  // Pagination
  page: number;
  rowsPerPage: number;
  totalCount: number;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  
  // Mobile support
  renderMobileCard?: (item: T) => React.ReactNode;
  mobileSortOptions?: { key: string; label: string }[];
  
  // Empty state
  emptyStateComponent?: React.ReactNode;
  
  // Styling
  tableAriaLabel?: string;
  className?: string;
}

// Mobile Sort Component
const MobileSortControls: React.FC<{
  sortConfig: SortConfig;
  onSort: (key: string) => void;
  sortOptions: { key: string; label: string }[];
}> = ({ sortConfig, onSort, sortOptions }) => {
  return (
    <Box sx={{ mb: 2 }}>
      <FormControl fullWidth size="small">
        <InputLabel>Sort by</InputLabel>
        <Select
          value={sortConfig.key}
          label="Sort by"
          onChange={(e) => onSort(e.target.value)}
          startAdornment={<SortIcon sx={{ mr: 1 }} />}
        >
          {sortOptions.map(option => (
            <MenuItem key={option.key} value={option.key}>
              {option.label} {sortConfig.key === option.key && (
                sortConfig.direction === 'asc' ? '↑' : '↓'
              )}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

function DataTable<T extends { id: string }>({
  data,
  columns,
  sortConfig,
  onSort,
  onRowClick,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  renderMobileCard,
  mobileSortOptions = [],
  emptyStateComponent,
  tableAriaLabel = 'data table',
  className
}: DataTableProps<T>) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Show empty state if no data
  if (data.length === 0 && totalCount === 0 && emptyStateComponent) {
    return (
      <Box className={className}>
        {emptyStateComponent}
      </Box>
    );
  }

  // Mobile view
  if (isMobile) {
    return (
      <Box className={className}>
        {/* Mobile Sort Controls */}
        {mobileSortOptions.length > 0 && (
          <MobileSortControls 
            sortConfig={sortConfig}
            onSort={onSort}
            sortOptions={mobileSortOptions}
          />
        )}

        {/* Mobile Cards */}
        <Box>
          {data.map((item) => (
            <Box key={item.id} onClick={() => onRowClick && onRowClick(item)}>
              {renderMobileCard ? renderMobileCard(item) : (
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography>Mobile card renderer not provided</Typography>
                </Paper>
              )}
            </Box>
          ))}
        </Box>

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          sx={{
            '& .MuiTablePagination-toolbar': {
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 1, sm: 0 }
            },
            '& .MuiTablePagination-spacer': {
              display: { xs: 'none', sm: 'flex' }
            }
          }}
        />
      </Box>
    );
  }

  // Desktop table view
  return (
    <Box className={className}>
      <TableContainer 
        component={Paper} 
        sx={{ borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
      >
        <Table stickyHeader aria-label={tableAriaLabel}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  align={column.align || 'left'}
                  onClick={column.sortable !== false ? () => onSort(column.sortKey || column.key) : undefined}
                  sx={{ 
                    cursor: column.sortable !== false ? 'pointer' : 'default',
                    width: column.width
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {column.label}
                    {column.sortable !== false && sortConfig.key === (column.sortKey || column.key) && (
                      sortConfig.direction === 'asc' ? 
                        <ArrowUpIcon fontSize="small" /> : 
                        <ArrowDownIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item) => (
              <TableRow 
                key={item.id}
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
                onClick={() => onRowClick && onRowClick(item)}
              >
                {columns.map((column) => (
                  <TableCell 
                    key={column.key}
                    align={column.align || 'left'}
                    component={column.key === columns[0].key ? "th" : "td"}
                    scope={column.key === columns[0].key ? "row" : undefined}
                  >
                    {column.render ? column.render(item) : (
                      <Typography>
                        {String((item as any)[column.key] || '')}
                      </Typography>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        sx={{
          '& .MuiTablePagination-toolbar': {
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 0 }
          },
          '& .MuiTablePagination-spacer': {
            display: { xs: 'none', sm: 'flex' }
          }
        }}
      />
    </Box>
  );
}

export default DataTable; 